import { resolveArtworkUrl, PRIORITY_COLORS } from "@/lib/api";
import { Heart, Plus, ExternalLink, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

function WishlistCard({ item, onSelect, index }) {
    const cheapest = (item.vendors || []).reduce((best, v) => {
        if (v.price == null) return best;
        if (best == null || v.price < best.price) return v;
        return best;
    }, null);
    return (
        <button
            onClick={() => onSelect(item)}
            className="tactile-card rounded-md overflow-hidden text-left fade-up group"
            style={{ animationDelay: `${Math.min(index * 30, 300)}ms` }}
            data-testid={`wishlist-card-${item.id}`}
        >
            <div className="aspect-square bg-secondary overflow-hidden relative">
                <img
                    src={resolveArtworkUrl(item)}
                    alt={`${item.album_title} by ${item.artist}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                />
                <div className="absolute top-3 right-3 flex gap-2">
                    <span className={`px-2.5 py-1 text-xs rounded-full border backdrop-blur-md bg-white/80 ${PRIORITY_COLORS[item.priority] || ""}`}>
                        {item.priority}
                    </span>
                </div>
                {item.discogs_id && (
                    <div
                        className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono rounded-full bg-foreground/85 text-background backdrop-blur-md border border-white/10"
                        data-testid={`wishlist-discogs-badge-${item.id}`}
                    >
                        <BadgeCheck className="w-3 h-3" strokeWidth={2} />
                        DISCOGS
                    </div>
                )}
            </div>
            <div className="p-4 space-y-2">
                <p className="overline text-muted-foreground">{item.artist}</p>
                <h3 className="font-display font-semibold text-lg leading-tight line-clamp-2 tracking-tight">
                    {item.album_title}
                </h3>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                    {item.target_price != null && (
                        <span className="text-xs font-mono text-muted-foreground">
                            Target · £{item.target_price.toFixed(2)}
                        </span>
                    )}
                    {cheapest && (
                        <span className="text-xs font-mono text-primary">
                            Best · £{cheapest.price.toFixed(2)} <span className="text-muted-foreground">({cheapest.vendor})</span>
                        </span>
                    )}
                    {(item.vendors?.length || 0) > 0 && (
                        <span className="text-[10px] rounded-full border border-border bg-muted text-muted-foreground px-2 py-0.5 font-mono">
                            {item.vendors.length} vendor{item.vendors.length > 1 ? "s" : ""}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

function EmptyState({ onAdd }) {
    return (
        <div className="tactile-card rounded-md p-12 text-center" data-testid="wishlist-empty-state">
            <Heart className="w-16 h-16 text-primary/60 mx-auto mb-6" strokeWidth={1} />
            <h3 className="font-display text-3xl font-bold tracking-tight">Nothing on the hunt.</h3>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
                Add records you&apos;re chasing — search Discogs, save vendor links, watch prices, and buy when you&apos;re ready.
            </p>
            <Button
                onClick={onAdd}
                className="mt-6 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="wishlist-empty-add-btn"
            >
                <Plus className="w-4 h-4 mr-2" />
                Add your first wish
            </Button>
        </div>
    );
}

export default function WishlistGrid({ items, loading, onSelect, onAdd }) {
    if (loading) return null;
    if (items.length === 0) return <EmptyState onAdd={onAdd} />;
    return (
        <section
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 sm:gap-6 lg:gap-8"
            data-testid="wishlist-grid"
        >
            {items.map((v, i) => (
                <WishlistCard key={v.id} item={v} onSelect={onSelect} index={i} />
            ))}
        </section>
    );
}
